import { classifyAsEntries } from '@collectivehealth/utils';

import R from 'ramda';
import app from 'app';
import moment from 'moment';

import templateUrl from './timeline.html';
import './timeline.scss';

import mxPopover from 'components/tooltips/mxPopover/mxPopover';

import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-refresh';
import PLAN_TYPES from 'constants/planTypes';

import { logEvent, eventCategories } from 'helpers/analytics';

function TimelineCtrl($state, Claims, PlanMembership) {
  const Timeline = this;

  // ----- Private Data --------------------------------------------------------

  let pagesLoaded = 0;

  const pageSize = 20;

  /**
   * Complete list of all claim objects visible on the page at the same time.
   *
   * @type {array}
   */
  let claims = [];

  /**
   * Maps plan type to actual API params that contain more than one specific
   * type.
   *
   * @type {object}
   */
  const typeMap = {
    medical: [
      'medicalClaim',
      'institutionalMedicalClaim',
      'professionalMedicalClaim'
    ],
    vision: ['visionClaim'],
    pharmacy: ['pharmacyClaim'],
    dental: ['dentalClaim']
  };

  function getOption(planType) {
    return {
      label: planType || 'all statements',
      value: planType
    };
  }

  Timeline.selectPlanType = planTypeOption => {
    Timeline.selectedPlanType = planTypeOption;
    logEvent('claimsPlanFiltered', {
      category: eventCategories.ACTIVITY,
      planType: planTypeOption.value || 'all'
    });
    $state.go('Activity', { planType: Timeline.selectedPlanType.value });
  };

  Timeline.canOpenClaim = function(claim) {
    return claim.claimType !== 'pharmacyClaim';
  };

  Timeline.viewClaimDetail = function(claim) {
    if (Timeline.canOpenClaim(claim)) {
      logEvent('digitalMBSViewed', {
        category: eventCategories.ACTIVITY,
        source: 'activity',
        planType: claim.planType
      });
      $state.go('Claim', { claimId: claim.id });
    }
  };

  Timeline.getClaimDisplayStatus = function(claim) {
    if (
      claim.claimSystemClaimStatus &&
      claim.claimSystemClaimStatus === 'DENIED'
    ) {
      return 'denied';
    } else if (claim.isAdjusted) {
      return 'adjusted';
    } else {
      return '';
    }
  };

  Timeline.loadMoreClaims = () => {
    Timeline.isLoadingClaims = true;

    // Patient ID needs to be undefined in the API if we want claims for all in
    // the family but our UI control needs it to be 0 so mapping to undefined here.
    const patientId =
      $state.params.patientId !== 0 ? $state.params.patientId : undefined;

    Claims.getClaims(Timeline.currentUser.user.id, {
      patientId,
      type: typeMap[$state.params.planType],
      sponsorId: Timeline.currentSponsorship.sponsor.id,
      skip: pagesLoaded * pageSize,
      limit: pageSize
    })
      .then(resp => {
        pagesLoaded += 1;

        // Create and save a new array of claims, removing any duplicates.
        claims = R.uniqBy(R.prop('id'), claims.concat(resp.claims));

        Timeline.maxResultsReached = claims.length >= resp.count;

        // From the new authoritative list of claims, (re)build a data strucutre
        // of claims grouped by year, then by month.
        Timeline.claims = classifyAsEntries(
          [
            // Group claims by year in descending order.
            [
              claim =>
                moment(claim.dateOfServiceStart, 'YYYY-MM-DD').format('YYYY'),
              R.comparator((a, b) => Number(a) > Number(b))
            ],
            // Group claims by month in descending order.
            [
              claim =>
                moment(claim.dateOfServiceStart, 'YYYY-MM-DD').format('MMM'),
              R.comparator((a, b) => moment(a, 'MMM').isAfter(moment(b, 'MMM')))
            ]
          ],
          claims
        );
      })
      .catch(error => {
        // @TODO - remove debug number
        // This is the error thrown by the API when skip is >= to the number of results.
        // This appears to be a general error, so when the API is updated we should handle
        // this more explicitly.
        if (error.data && error.data.debugNumber === 2760461719) {
          Timeline.maxResultsReached = true;
        }
      })
      .then(() => {
        Timeline.isInitialDataLoaded = true;
        Timeline.isLoadingClaims = false;
      });
  };

  Timeline.$onInit = () => {
    PlanMembership.getPlanMemberships(Timeline.currentSponsorship.id).then(
      planMemberships => {
        Timeline.planTypesForSelect = planMemberships.allPossiblePlanTypes.map(
          planType => {
            return { label: planType, value: planType };
          }
        );
        Timeline.planTypesForSelect.unshift({
          label: 'all statements',
          value: ''
        });
      }
    );

    Timeline.selectedPlanType = getOption($state.params.planType);

    Timeline.planTypes = PLAN_TYPES.orderedWithPharmacy;

    Timeline.years = [];

    Timeline.maxResultsReached = false;

    Timeline.responsibilityPopover = {
      mouseleave() {
        logEvent('tooltipViewed', {
          tooltipName: 'yourResponsibility',
          page: 'activity',
          category: eventCategories.ACTIVITY
        });
      },
      component: mxPopover,
      locals: {
        content: 'Your total responsibility for the current plan year'
      },
      attachment: 'top right',
      targetAttachment: 'bottom right',
      offset: '-4px 0'
    };

    Timeline.loadMoreClaims();
  };
}

app.component('timeline', {
  bindings: {
    currentSponsorship: '<',
    currentUser: '<',
    hasFamily: '<'
  },
  controller: TimelineCtrl,
  controllerAs: 'Timeline',
  templateUrl
});

export default 'timeline';
